/* eslint-disable react-refresh/only-export-components -- Table config exports builder utilities, not React components. */
import type { ColumnsType } from "antd/es/table/interface";
import { Link } from "react-router";
import LocalAssigneeSelect from "./LocalAssigneeSelect";
import { buildColumns, renderDateCell, renderResolutionTagCell } from "./utils";

export type TableRow = Record<string, string | number | boolean | null>;

const SEARCHABLE_COLUMNS = new Set(["issue_id", "subject", "site", "assignee"]);
const FIXED_COLUMNS = new Set(["issue_id"]);

const HIDDEN_COLUMNS = new Set([
  "subject",
  "participant_id",
  "site",
  "study_status",
  "discrepancy_trial_visit",
  "discrepancy_level",
  "discrepancy_type",
  "discrepancy_category",
  "discrepancy_subcategory",
  "discrepancy_details",
  "discrepancy_vivo_status",
  "assignee",
  "last_modified_by",
  "discrepancy_last_modified_by",
  "discrepancy_assignment_last_modified_by",
  "discrepancy_assignments_last_modified_datetime",
  "discrepancy_id",
  "discrepancy_rule_id",
]);

export const filterHiddenColumns = (rows: TableRow[]): TableRow[] => {
  return rows.map(
    (row) =>
      Object.fromEntries(
        Object.entries(row).filter(([key]) => !HIDDEN_COLUMNS.has(key)),
      ) as TableRow,
  );
};

export const ISSUES_COLUMN_ORDER = [
  "issue_id",
  "discrepancy_description",
  "discrepancy_priority",
  "discrepancy_resolution_status",
  "discrepancy_data_source",
  "discrepancy_identification_datetime",
  "discrepancy_assignee",
  "discrepancy_days_open",
  "discrepancy_resolution_datetime",
] as const;

const TITLE_MAPPING = {
  issue_id: "Issue ID",
  last_modified_by: "Last Modified By",
  discrepancy_id: "Discrepancy ID",
  discrepancy_rule_id: "Discrepancy Rule ID",
  participant_id: "Participant ID",
  study_status: "Study Status",
  discrepancy_trial_visit: "Visit",
  discrepancy_data_source: "Data Source",
  discrepancy_level: "Level",
  discrepancy_type: "Type",
  discrepancy_category: "Category",
  discrepancy_subcategory: "Subcategory",
  discrepancy_description: "Description",
  discrepancy_details: "Details",
  discrepancy_vivo_status: "Vivo Status",
  discrepancy_identification_datetime: "Identification Datetime",
  discrepancy_resolution_datetime: "Resolution Datetime",
  discrepancy_resolution_status: "Resolution Status",
  discrepancy_days_open: "Days Open",
  discrepancy_priority: "Priority",
  discrepancy_assignee: "Assignee",
  discrepancy_last_modified_by: "Discrepancy Last Modified By",
  discrepancy_assignment_last_modified_by: "Assignment Last Modified By",
  discrepancy_assignments_last_modified_datetime:
    "Assignment Last Modified Datetime",
  assignee: "Assignee",
} satisfies Record<string, string>;

const COLUMN_WIDTHS = {
  DEFAULT: 150,
  issue_id: 130,
  subject: 120,
  site: 90,
  study_status: 140,
  discrepancy_trial_visit: 140,
  discrepancy_data_source: 180,
  discrepancy_category: 220,
  discrepancy_subcategory: 260,
  discrepancy_description: 320,
  discrepancy_details: 360,
  discrepancy_identification_datetime: 210,
  discrepancy_resolution_status: 180,
  discrepancy_resolution_datetime: 210,
  discrepancy_assignments_last_modified_datetime: 230,
  discrepancy_assignee: 250,
  discrepancy_days_open: 100,
} satisfies Record<string, number>;

const renderMap: Record<string, (text: unknown) => React.ReactNode> = {
  discrepancy_vivo_status: renderResolutionTagCell,
  discrepancy_resolution_status: renderResolutionTagCell,
  discrepancy_identification_datetime: renderDateCell,
  discrepancy_resolution_datetime: renderDateCell,
  discrepancy_assignments_last_modified_datetime: renderDateCell,
};

export const createIssuesColumns = (
  rows: TableRow[],
  buildUrl?: (path?: string) => string,
  assigneeOptions: string[] = [],
  onAssigneeChange?: (issueId: string, nextAssignee: string | null) => void,
): ColumnsType<TableRow> => {
  return buildColumns({
    rows,
    columnOrder: ISSUES_COLUMN_ORDER,
    titleMapping: TITLE_MAPPING,
    columnWidths: COLUMN_WIDTHS,
    searchableColumns: SEARCHABLE_COLUMNS,
    fixedColumns: FIXED_COLUMNS,
    hiddenColumns: HIDDEN_COLUMNS,
    renderMap,
    customRender: (header, text, record) => {
      if (header === "subject" && text) {
        return (
          <Link
            className="text-[#08709B]"
            to={
              buildUrl?.(`subjects/overview/${text}/data-discrepancies`) ?? "#"
            }
          >
            {text as string}
          </Link>
        );
      }

      if (
        header === "discrepancy_assignee" &&
        typeof record?.discrepancy_id === "string" &&
        onAssigneeChange
      ) {
        return (
          <LocalAssigneeSelect
            value={text as string | number | boolean | null}
            options={assigneeOptions}
            onChange={(nextAssignee) =>
              onAssigneeChange(record.discrepancy_id as string, nextAssignee)
            }
          />
        );
      }

      return undefined;
    },
  });
};
